import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintAnesthesiaMedication = makeArticle({
  slug: "does-saint-tms-require-anesthesia-or-medication",
  title: "Does SAINT® TMS Require Anesthesia or Medication? | Neuro Wellness",
  description:
    "SAINT® TMS does not require anesthesia or medication. It is a non-invasive, drug-free brain stimulation treatment. Learn why no sedation is used and how this differs from ECT and SPRAVATO®.",
  canonical: "/learn/does-saint-tms-require-anesthesia-or-medication",
  h1: "Does SAINT® TMS Require Anesthesia or Medication?",
  breadcrumbLabel: "SAINT® Anesthesia & Medication",
  shortAnswer:
    "No. SAINT® TMS does not require anesthesia, sedation, or medication. It is a non-invasive brain stimulation treatment that uses magnetic pulses while the patient is fully awake and alert. This is a key difference from ECT, which requires general anesthesia, and from SPRAVATO® (esketamine), which causes dissociation and requires monitoring. SAINT® introduces no chemical into the body. Some patients continue taking antidepressant medication during SAINT®, but the treatment itself is drug-free.",
  keyTakeaways: [
    "SAINT® does not require anesthesia or sedation.",
    "Patients are awake and alert throughout all sessions.",
    "SAINT® introduces no chemical into the body.",
    "No driving restriction after sessions — unlike ECT and SPRAVATO®.",
    "Some patients continue antidepressants during SAINT®, but the treatment itself is drug-free.",
  ],
  sections: [
    {
      heading: "No Anesthesia",
      paragraphs: [
        "SAINT® is delivered while the patient is fully awake. No general anesthesia, sedation, or local anesthesia is used. The magnetic pulses stimulate the brain through the scalp without inducing a seizure or altering consciousness. This is fundamentally different from ECT, which requires general anesthesia and induces a controlled seizure.",
        "Because there is no anesthesia, there is no recovery period, no need for fasting before treatment, and no need for an escort to drive you home. Patients can drive themselves to and from the clinic and resume normal activities after each session.",
      ],
    },
    {
      heading: "No Medication Required",
      paragraphs: [
        "SAINT® itself introduces no chemical into the body. It is a device-based treatment that uses magnetic pulses, not a drug. This means SAINT® does not cause the systemic side effects associated with medications — such as weight gain, sexual dysfunction, gastrointestinal issues, or drowsiness.",
        "Some patients continue taking their existing antidepressant medication during SAINT® treatment. Whether to continue, adjust, or discontinue medication is a clinical decision made by the psychiatrist. SAINT® does not require you to stop or start any medication, but your psychiatrist will review your current medications as part of the evaluation.",
      ],
    },
    {
      heading: "How This Compares to Other Treatments",
      paragraphs: [
        "ECT requires general anesthesia and induces a controlled seizure, with a recovery period and potential memory side effects. SPRAVATO® (esketamine) is a nasal spray that causes dissociation and sedation, requiring at least two hours of monitoring and a driver home. SAINT® requires none of these — it is delivered in an outpatient setting with no anesthesia, no sedation, and no driving restriction.",
      ],
      links: [
        { to: "/learn/saint-vs-spravato", text: "Compare SAINT® vs SPRAVATO®" },
      ],
    },
  ],
  faqs: [
    {
      question: "Does SAINT TMS require anesthesia?",
      answer:
        "No. SAINT® is delivered while the patient is fully awake and alert. No anesthesia or sedation is used. The magnetic pulses stimulate the brain without inducing a seizure or altering consciousness. This is different from ECT, which requires general anesthesia.",
    },
    {
      question: "Does SAINT TMS require medication?",
      answer:
        "No. SAINT® is a device-based treatment that introduces no chemical into the body. Some patients continue taking their existing antidepressant medication during SAINT®, but the treatment itself is drug-free. Whether to continue or adjust medication is a clinical decision made by your psychiatrist.",
    },
    {
      question: "Can I drive after SAINT since there's no anesthesia?",
      answer:
        "Yes. Because SAINT® does not use anesthesia or sedation and does not impair alertness, patients can typically drive themselves to and from the clinic. This differs from ECT and SPRAVATO®, which both require an escort home.",
    },
    {
      question: "Will I need to stop my antidepressants for SAINT?",
      answer:
        "Not necessarily. Whether to continue, adjust, or discontinue medication during SAINT® is a clinical decision made by your psychiatrist. Many patients continue their current medication. Do not stop or change medication on your own — always discuss with your prescribing clinician.",
    },
    {
      question: "Does SAINT have systemic side effects like medications?",
      answer:
        "No. Because SAINT® introduces no chemical into the body, it does not cause systemic side effects like weight gain, sexual dysfunction, or gastrointestinal issues that are common with antidepressant medications. SAINT®'s side effects are localized to the treatment site — headache and scalp discomfort.",
    },
  ],
  sources: [],
  relatedLinks: saintQuestionRelated,
});
